import { Language } from '../types';

export interface DelegationCardTranslations {
  tell: { name: string; description: string };
  sell: { name: string; description: string };
  consult: { name: string; description: string };
  agree: { name: string; description: string };
  advise: { name: string; description: string };
  inquire: { name: string; description: string };
  delegate: { name: string; description: string };
}

export interface UITranslations {
  home: {
    title: string;
    subtitle: string;
    createRoomTitle: string;
    createRoomDescription: string;
    createRoom: string;
    joinRoomTitle: string;
    joinRoomDescription: string;
    joinRoom: string;
    whatIsTitle: string;
    description1: string;
    description2: string;
  };
  createRoom: {
    title: string;
    subtitle: string;
    roomName: string;
    roomNamePlaceholder: string;
    playerName: string;
    playerNamePlaceholder: string;
    email: string;
    emailPlaceholder: string;
    create: string;
    cancel: string;
  };
  joinRoom: {
    title: string;
    subtitle: string;
    roomCode: string;
    roomCodePlaceholder: string;
    playerName: string;
    playerNamePlaceholder: string;
    email: string;
    emailPlaceholder: string;
    join: string;
    cancel: string;
  };
  room: {
    title: string;
    situations: string;
    addSituation: string;
    situationTitle: string;
    situationTitlePlaceholder: string;
    status: string;
    actions: string;
    startVoting: string;
    removeSituation: string;
    open: string;
    voting: string;
    closed: string;
    votingEndsIn: string;
    selectCard: string;
    voted: string;
    votingResults: string;
    votes: string;
    noSituations: string;
    addFirstSituation: string;
    players: string;
  };
}

export const delegationCardTranslations: Record<Language, DelegationCardTranslations> = {
  en: {
    tell: {
      name: 'Tell',
      description: 'I will tell them what to do',
    },
    sell: {
      name: 'Sell',
      description: 'I will try and sell it to them',
    },
    consult: {
      name: 'Consult',
      description: 'I will consult and then decide',
    },
    agree: {
      name: 'Agree',
      description: 'We will agree together',
    },
    advise: {
      name: 'Advise',
      description: 'I will advise but they decide',
    },
    inquire: {
      name: 'Inquire',
      description: 'I will inquire after they decide',
    },
    delegate: {
      name: 'Delegate',
      description: 'I will fully delegate',
    },
  },
  pt: {
    tell: {
      name: 'Dizer',
      description: 'Vou dizer o que fazer',
    },
    sell: {
      name: 'Vender',
      description: 'Vou tentar vender a ideia',
    },
    consult: {
      name: 'Consultar',
      description: 'Vou consultar e depois decidir',
    },
    agree: {
      name: 'Concordar',
      description: 'Vamos concordar juntos',
    },
    advise: {
      name: 'Aconselhar',
      description: 'Vou aconselhar mas eles decidem',
    },
    inquire: {
      name: 'Perguntar',
      description: 'Vou perguntar depois que decidirem',
    },
    delegate: {
      name: 'Delegar',
      description: 'Vou delegar completamente',
    },
  },
  es: {
    tell: {
      name: 'Decir',
      description: 'Les diré qué hacer',
    },
    sell: {
      name: 'Vender',
      description: 'Intentaré venderles la idea',
    },
    consult: {
      name: 'Consultar',
      description: 'Consultaré y luego decidiré',
    },
    agree: {
      name: 'Acordar',
      description: 'Acordaremos juntos',
    },
    advise: {
      name: 'Aconsejar',
      description: 'Aconsejaré pero ellos deciden',
    },
    inquire: {
      name: 'Preguntar',
      description: 'Preguntaré después de que decidan',
    },
    delegate: {
      name: 'Delegar',
      description: 'Delegaré completamente',
    },
  },
  fr: {
    tell: {
      name: 'Dire',
      description: 'Je leur dirai quoi faire',
    },
    sell: {
      name: 'Vendre',
      description: 'J\'essaierai de leur vendre l\'idée',
    },
    consult: {
      name: 'Consulter',
      description: 'Je consulterai puis déciderai',
    },
    agree: {
      name: 'Convenir',
      description: 'Nous conviendrons ensemble',
    },
    advise: {
      name: 'Conseiller',
      description: 'Je conseillerai mais ils décident',
    },
    inquire: {
      name: 'Demander',
      description: 'Je demanderai après qu\'ils décident',
    },
    delegate: {
      name: 'Déléguer',
      description: 'Je déléguerai complètement',
    },
  },
  de: {
    tell: {
      name: 'Sagen',
      description: 'Ich werde ihnen sagen, was zu tun ist',
    },
    sell: {
      name: 'Verkaufen',
      description: 'Ich werde versuchen, es ihnen zu verkaufen',
    },
    consult: {
      name: 'Konsultieren',
      description: 'Ich werde konsultieren und dann entscheiden',
    },
    agree: {
      name: 'Zustimmen',
      description: 'Wir werden gemeinsam zustimmen',
    },
    advise: {
      name: 'Beraten',
      description: 'Ich werde beraten, aber sie entscheiden',
    },
    inquire: {
      name: 'Anfragen',
      description: 'Ich werde nachfragen, nachdem sie entschieden haben',
    },
    delegate: {
      name: 'Delegieren',
      description: 'Ich werde vollständig delegieren',
    },
  },
};

export const uiTranslations: Record<Language, UITranslations> = {
  en: {
    home: {
      title: 'Delegation Poker',
      subtitle: 'Discover the right level of delegation for your team decisions',
      createRoomTitle: 'Create a Room',
      createRoomDescription: 'Start a new delegation poker session and invite your team',
      createRoom: 'Create Room',
      joinRoomTitle: 'Join a Room',
      joinRoomDescription: 'Join an existing session with a room code',
      joinRoom: 'Join Room',
      whatIsTitle: 'What is Delegation Poker?',
      description1:
        'Delegation Poker is a practice that helps teams and managers agree on the level of delegation for different types of decisions. It uses seven delegation levels, from "Tell" (full authority) to "Delegate" (full autonomy).',
      description2:
        'By playing delegation poker, teams can have explicit conversations about authority and autonomy, leading to better alignment and fewer misunderstandings.',
    },
    createRoom: {
      title: 'Create a Room',
      subtitle: 'Set up a new delegation poker session',
      roomName: 'Room Name',
      roomNamePlaceholder: 'Enter room name',
      playerName: 'Your Name',
      playerNamePlaceholder: 'Enter your name',
      email: 'Email',
      emailPlaceholder: 'Enter your email',
      create: 'Create Room',
      cancel: 'Cancel',
    },
    joinRoom: {
      title: 'Join a Room',
      subtitle: 'Join an existing delegation poker session',
      roomCode: 'Room Code',
      roomCodePlaceholder: 'Enter room code',
      playerName: 'Your Name',
      playerNamePlaceholder: 'Enter your name',
      email: 'Email',
      emailPlaceholder: 'Enter your email',
      join: 'Join Room',
      cancel: 'Cancel',
    },
    room: {
      title: 'Delegation Poker Room',
      situations: 'Situations',
      addSituation: 'Add Situation',
      situationTitle: 'Situation',
      situationTitlePlaceholder: 'Enter situation description',
      status: 'Status',
      actions: 'Actions',
      startVoting: 'Start Voting',
      removeSituation: 'Remove',
      open: 'Open',
      voting: 'Voting',
      closed: 'Closed',
      votingEndsIn: 'Voting ends in',
      selectCard: 'Select a delegation level',
      voted: 'You voted',
      votingResults: 'Voting Results',
      votes: 'votes',
      noSituations: 'No situations yet',
      addFirstSituation: 'Add your first situation to start delegation poker',
      players: 'Players',
    },
  },
  pt: {
    home: {
      title: 'Delegation Poker',
      subtitle: 'Descubra o nível certo de delegação para as decisões da sua equipe',
      createRoomTitle: 'Criar uma Sala',
      createRoomDescription: 'Inicie uma nova sessão de delegation poker e convide sua equipe',
      createRoom: 'Criar Sala',
      joinRoomTitle: 'Entrar em uma Sala',
      joinRoomDescription: 'Entre em uma sessão existente com um código de sala',
      joinRoom: 'Entrar na Sala',
      whatIsTitle: 'O que é Delegation Poker?',
      description1:
        'Delegation Poker é uma prática que ajuda equipes e gestores a concordar sobre o nível de delegação para diferentes tipos de decisões. Usa sete níveis de delegação, desde "Dizer" (autoridade total) até "Delegar" (autonomia total).',
      description2:
        'Ao jogar delegation poker, as equipes podem ter conversas explícitas sobre autoridade e autonomia, levando a melhor alinhamento e menos mal-entendidos.',
    },
    createRoom: {
      title: 'Criar uma Sala',
      subtitle: 'Configure uma nova sessão de delegation poker',
      roomName: 'Nome da Sala',
      roomNamePlaceholder: 'Digite o nome da sala',
      playerName: 'Seu Nome',
      playerNamePlaceholder: 'Digite seu nome',
      email: 'Email',
      emailPlaceholder: 'Digite seu email',
      create: 'Criar Sala',
      cancel: 'Cancelar',
    },
    joinRoom: {
      title: 'Entrar em uma Sala',
      subtitle: 'Entre em uma sessão existente de delegation poker',
      roomCode: 'Código da Sala',
      roomCodePlaceholder: 'Digite o código da sala',
      playerName: 'Seu Nome',
      playerNamePlaceholder: 'Digite seu nome',
      email: 'Email',
      emailPlaceholder: 'Digite seu email',
      join: 'Entrar na Sala',
      cancel: 'Cancelar',
    },
    room: {
      title: 'Sala de Delegation Poker',
      situations: 'Situações',
      addSituation: 'Adicionar Situação',
      situationTitle: 'Situação',
      situationTitlePlaceholder: 'Descreva a situação',
      status: 'Status',
      actions: 'Ações',
      startVoting: 'Iniciar Votação',
      removeSituation: 'Remover',
      open: 'Aberta',
      voting: 'Votando',
      closed: 'Fechada',
      votingEndsIn: 'Votação termina em',
      selectCard: 'Selecione um nível de delegação',
      voted: 'Você votou',
      votingResults: 'Resultados da Votação',
      votes: 'votos',
      noSituations: 'Nenhuma situação ainda',
      addFirstSituation: 'Adicione sua primeira situação para começar o delegation poker',
      players: 'Jogadores',
    },
  },
  es: {
    home: {
      title: 'Delegation Poker',
      subtitle: 'Descubre el nivel correcto de delegación para las decisiones de tu equipo',
      createRoomTitle: 'Crear una Sala',
      createRoomDescription: 'Inicia una nueva sesión de delegation poker e invita a tu equipo',
      createRoom: 'Crear Sala',
      joinRoomTitle: 'Unirse a una Sala',
      joinRoomDescription: 'Únete a una sesión existente con un código de sala',
      joinRoom: 'Unirse a la Sala',
      whatIsTitle: '¿Qué es Delegation Poker?',
      description1:
        'Delegation Poker es una práctica que ayuda a equipos y gerentes a acordar el nivel de delegación para diferentes tipos de decisiones. Utiliza siete niveles de delegación, desde "Decir" (autoridad total) hasta "Delegar" (autonomía total).',
      description2:
        'Al jugar delegation poker, los equipos pueden tener conversaciones explícitas sobre autoridad y autonomía, lo que lleva a una mejor alineación y menos malentendidos.',
    },
    createRoom: {
      title: 'Crear una Sala',
      subtitle: 'Configura una nueva sesión de delegation poker',
      roomName: 'Nombre de la Sala',
      roomNamePlaceholder: 'Ingresa el nombre de la sala',
      playerName: 'Tu Nombre',
      playerNamePlaceholder: 'Ingresa tu nombre',
      email: 'Email',
      emailPlaceholder: 'Ingresa tu email',
      create: 'Crear Sala',
      cancel: 'Cancelar',
    },
    joinRoom: {
      title: 'Unirse a una Sala',
      subtitle: 'Únete a una sesión existente de delegation poker',
      roomCode: 'Código de Sala',
      roomCodePlaceholder: 'Ingresa el código de sala',
      playerName: 'Tu Nombre',
      playerNamePlaceholder: 'Ingresa tu nombre',
      email: 'Email',
      emailPlaceholder: 'Ingresa tu email',
      join: 'Unirse a la Sala',
      cancel: 'Cancelar',
    },
    room: {
      title: 'Sala de Delegation Poker',
      situations: 'Situaciones',
      addSituation: 'Añadir Situación',
      situationTitle: 'Situación',
      situationTitlePlaceholder: 'Describe la situación',
      status: 'Estado',
      actions: 'Acciones',
      startVoting: 'Iniciar Votación',
      removeSituation: 'Eliminar',
      open: 'Abierta',
      voting: 'Votando',
      closed: 'Cerrada',
      votingEndsIn: 'La votación termina en',
      selectCard: 'Selecciona un nivel de delegación',
      voted: 'Has votado',
      votingResults: 'Resultados de la Votación',
      votes: 'votos',
      noSituations: 'No hay situaciones aún',
      addFirstSituation: 'Añade tu primera situación para comenzar el delegation poker',
      players: 'Jugadores',
    },
  },
  fr: {
    home: {
      title: 'Delegation Poker',
      subtitle: 'Découvrez le bon niveau de délégation pour les décisions de votre équipe',
      createRoomTitle: 'Créer une Salle',
      createRoomDescription: 'Démarrez une nouvelle session de delegation poker et invitez votre équipe',
      createRoom: 'Créer une Salle',
      joinRoomTitle: 'Rejoindre une Salle',
      joinRoomDescription: 'Rejoignez une session existante avec un code de salle',
      joinRoom: 'Rejoindre la Salle',
      whatIsTitle: 'Qu\'est-ce que Delegation Poker ?',
      description1:
        'Delegation Poker est une pratique qui aide les équipes et les managers à s\'accorder sur le niveau de délégation pour différents types de décisions. Il utilise sept niveaux de délégation, de "Dire" (autorité totale) à "Déléguer" (autonomie totale).',
      description2:
        'En jouant au delegation poker, les équipes peuvent avoir des conversations explicites sur l\'autorité et l\'autonomie, ce qui conduit à un meilleur alignement et moins de malentendus.',
    },
    createRoom: {
      title: 'Créer une Salle',
      subtitle: 'Configurez une nouvelle session de delegation poker',
      roomName: 'Nom de la Salle',
      roomNamePlaceholder: 'Entrez le nom de la salle',
      playerName: 'Votre Nom',
      playerNamePlaceholder: 'Entrez votre nom',
      email: 'Email',
      emailPlaceholder: 'Entrez votre email',
      create: 'Créer la Salle',
      cancel: 'Annuler',
    },
    joinRoom: {
      title: 'Rejoindre une Salle',
      subtitle: 'Rejoignez une session existante de delegation poker',
      roomCode: 'Code de Salle',
      roomCodePlaceholder: 'Entrez le code de salle',
      playerName: 'Votre Nom',
      playerNamePlaceholder: 'Entrez votre nom',
      email: 'Email',
      emailPlaceholder: 'Entrez votre email',
      join: 'Rejoindre la Salle',
      cancel: 'Annuler',
    },
    room: {
      title: 'Salle de Delegation Poker',
      situations: 'Situations',
      addSituation: 'Ajouter une Situation',
      situationTitle: 'Situation',
      situationTitlePlaceholder: 'Décrivez la situation',
      status: 'Statut',
      actions: 'Actions',
      startVoting: 'Démarrer le Vote',
      removeSituation: 'Supprimer',
      open: 'Ouverte',
      voting: 'Vote en cours',
      closed: 'Fermée',
      votingEndsIn: 'Le vote se termine dans',
      selectCard: 'Sélectionnez un niveau de délégation',
      voted: 'Vous avez voté',
      votingResults: 'Résultats du Vote',
      votes: 'votes',
      noSituations: 'Aucune situation pour le moment',
      addFirstSituation: 'Ajoutez votre première situation pour commencer le delegation poker',
      players: 'Joueurs',
    },
  },
  de: {
    home: {
      title: 'Delegation Poker',
      subtitle: 'Entdecken Sie das richtige Delegationsniveau für Ihre Teamentscheidungen',
      createRoomTitle: 'Einen Raum Erstellen',
      createRoomDescription: 'Starten Sie eine neue Delegation Poker-Sitzung und laden Sie Ihr Team ein',
      createRoom: 'Raum Erstellen',
      joinRoomTitle: 'Einem Raum Beitreten',
      joinRoomDescription: 'Treten Sie einer bestehenden Sitzung mit einem Raumcode bei',
      joinRoom: 'Raum Beitreten',
      whatIsTitle: 'Was ist Delegation Poker?',
      description1:
        'Delegation Poker ist eine Praxis, die Teams und Managern hilft, sich über das Delegationsniveau für verschiedene Arten von Entscheidungen zu einigen. Es verwendet sieben Delegationsstufen, von "Sagen" (volle Autorität) bis "Delegieren" (volle Autonomie).',
      description2:
        'Durch das Spielen von Delegation Poker können Teams explizite Gespräche über Autorität und Autonomie führen, was zu besserer Ausrichtung und weniger Missverständnissen führt.',
    },
    createRoom: {
      title: 'Einen Raum Erstellen',
      subtitle: 'Richten Sie eine neue Delegation Poker-Sitzung ein',
      roomName: 'Raumname',
      roomNamePlaceholder: 'Geben Sie den Raumnamen ein',
      playerName: 'Ihr Name',
      playerNamePlaceholder: 'Geben Sie Ihren Namen ein',
      email: 'Email',
      emailPlaceholder: 'Geben Sie Ihre Email ein',
      create: 'Raum Erstellen',
      cancel: 'Abbrechen',
    },
    joinRoom: {
      title: 'Einem Raum Beitreten',
      subtitle: 'Treten Sie einer bestehenden Delegation Poker-Sitzung bei',
      roomCode: 'Raumcode',
      roomCodePlaceholder: 'Geben Sie den Raumcode ein',
      playerName: 'Ihr Name',
      playerNamePlaceholder: 'Geben Sie Ihren Namen ein',
      email: 'Email',
      emailPlaceholder: 'Geben Sie Ihre Email ein',
      join: 'Raum Beitreten',
      cancel: 'Abbrechen',
    },
    room: {
      title: 'Delegation Poker Raum',
      situations: 'Situationen',
      addSituation: 'Situation Hinzufügen',
      situationTitle: 'Situation',
      situationTitlePlaceholder: 'Beschreiben Sie die Situation',
      status: 'Status',
      actions: 'Aktionen',
      startVoting: 'Abstimmung Starten',
      removeSituation: 'Entfernen',
      open: 'Offen',
      voting: 'Abstimmung läuft',
      closed: 'Geschlossen',
      votingEndsIn: 'Die Abstimmung endet in',
      selectCard: 'Wählen Sie ein Delegationsniveau',
      voted: 'Sie haben abgestimmt',
      votingResults: 'Abstimmungsergebnisse',
      votes: 'Stimmen',
      noSituations: 'Noch keine Situationen',
      addFirstSituation: 'Fügen Sie Ihre erste Situation hinzu, um Delegation Poker zu starten',
      players: 'Spieler',
    },
  },
};

export const getCardTranslation = (
  language: Language,
  cardId: string
): { name: string; description: string } => {
  const translations = delegationCardTranslations[language];
  return translations[cardId as keyof DelegationCardTranslations] || { name: cardId, description: '' };
};

export const getUITranslation = (language: Language): UITranslations => {
  return uiTranslations[language];
};
